import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';

export interface SubstrateAccount {
  address: string;
  name?: string;
  source: string;
}

export type SubstrateConnectionStatus = 'idle' | 'connecting' | 'connected' | 'error';

interface InjectedWeb3Window extends Window {
  injectedWeb3?: Record<string, unknown>;
}

export interface SubstrateConnectionContextValue {
  detectedExtensionNames: string[];
  accounts: SubstrateAccount[];
  selectedAccount: SubstrateAccount | null;
  status: SubstrateConnectionStatus;
  errorMessage: string | null;
  connect: () => Promise<void>;
  selectAccount: (address: string) => void;
  disconnect: () => void;
}

const SubstrateConnectionContext = createContext<SubstrateConnectionContextValue | undefined>(
  undefined,
);

const getDetectedExtensionNames = () => {
  if (typeof window === 'undefined') {
    return [];
  }

  return Object.keys((window as InjectedWeb3Window).injectedWeb3 ?? {}).sort();
};

export interface SubstrateConnectionProviderProps {
  children: ReactNode;
}

export const SubstrateConnectionProvider = ({ children }: SubstrateConnectionProviderProps) => {
  const [detectedExtensionNames, setDetectedExtensionNames] = useState(getDetectedExtensionNames);
  const [accounts, setAccounts] = useState<SubstrateAccount[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<SubstrateAccount | null>(null);
  const [status, setStatus] = useState<SubstrateConnectionStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const connect = useCallback(async () => {
    setStatus('connecting');
    setErrorMessage(null);
    setDetectedExtensionNames(getDetectedExtensionNames());

    try {
      const { web3Accounts, web3Enable } = await import('@polkadot/extension-dapp');
      const extensions = await web3Enable('Forge');

      if (extensions.length === 0) {
        throw new Error('No Bittensor wallet extension approved the connection.');
      }

      const extensionAccounts = await web3Accounts();
      const nextAccounts = extensionAccounts.map<SubstrateAccount>((account) => ({
        address: account.address,
        name: account.meta.name,
        source: account.meta.source,
      }));

      if (nextAccounts.length === 0) {
        throw new Error('No Substrate accounts were exposed by the connected extension.');
      }

      setAccounts(nextAccounts);
      setSelectedAccount(
        (currentSelectedAccount) =>
          nextAccounts.find((account) => account.address === currentSelectedAccount?.address) ??
          nextAccounts[0]!,
      );
      setStatus('connected');
    } catch (error) {
      setStatus('error');
      setErrorMessage(
        error instanceof Error ? error.message : 'Failed to connect Bittensor wallet.',
      );
    }
  }, []);

  const selectAccount = useCallback(
    (address: string) => {
      const account = accounts.find((candidate) => candidate.address === address);

      if (account) {
        setSelectedAccount(account);
      }
    },
    [accounts],
  );

  const disconnect = useCallback(() => {
    setAccounts([]);
    setSelectedAccount(null);
    setStatus('idle');
    setErrorMessage(null);
  }, []);

  const value = useMemo<SubstrateConnectionContextValue>(
    () => ({
      detectedExtensionNames,
      accounts,
      selectedAccount,
      status,
      errorMessage,
      connect,
      selectAccount,
      disconnect,
    }),
    [
      accounts,
      connect,
      detectedExtensionNames,
      disconnect,
      errorMessage,
      selectedAccount,
      status,
      selectAccount,
    ],
  );

  return (
    <SubstrateConnectionContext.Provider value={value}>
      {children}
    </SubstrateConnectionContext.Provider>
  );
};

export const useSubstrateConnection = () => {
  const context = useContext(SubstrateConnectionContext);

  if (!context) {
    throw new Error('useSubstrateConnection must be used inside SubstrateConnectionProvider');
  }

  return context;
};
